import {
  PuckSafeSelect as Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/cms-puck/fields/PuckSafeSelect";
import {
  getSocialPlatformOptions,
  normalizeSocialLinkItems,
  type SocialLinkItem,
  type SocialPlatform,
} from "@/lib/cms-puck/blocks/social-links";
import { CMS_PALETTE_TEXT_INPUT_CLASS } from "@/components/cms/theme/cms-color-input-styles";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cn } from "@/lib/utils";
import { Plus, Trash2 } from "lucide-react";

export function SocialLinksField({
  value,
  onChange,
}: {
  value: SocialLinkItem[] | undefined;
  onChange: (value: SocialLinkItem[]) => void;
}) {
  const cmsNew = useCmsNew();
  const items = normalizeSocialLinkItems(value);
  const createEmptyItem = (): SocialLinkItem => ({
    platform: "globe",
    url: "",
    label: cmsNew("cms.new.auto.label.d41d8cd9", ""),
  });
  const display = items.length > 0 ? items : [createEmptyItem()];

  const updateAt = (index: number, patch: Partial<SocialLinkItem>) => {
    const next = [...display];
    next[index] = { ...next[index], ...patch };
    onChange(
      next.filter(
        (it) => it.url.trim().length > 0 || it.label.trim().length > 0,
      ),
    );
  };

  const addItem = () => onChange([...display, createEmptyItem()]);
  const removeAt = (index: number) => {
    const next = display.filter((_, i) => i !== index);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {display.map((item, index) => (
        <div
          key={index}
          className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/50 p-3"
        >
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs font-medium text-slate-600">
              Lien {index + 1}
            </Label>
            {display.length > 1 ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-slate-400"
                onClick={() => removeAt(index)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </div>
          <Select
            value={item.platform}
            onValueChange={(v) =>
              updateAt(index, { platform: v as SocialPlatform })
            }
          >
            <SelectTrigger className="h-9 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {getSocialPlatformOptions().map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <input
            type="url"
            placeholder={cmsNew("cms.new.auto.placeholder.https", "https://…")}
            value={item.url}
            className={cn(CMS_PALETTE_TEXT_INPUT_CLASS, "h-9 w-full")}
            onChange={(e) => updateAt(index, { url: e.target.value })}
          />
          <input
            type="text"
            placeholder={cmsNew(
              "cms.new.auto.placeholder.libelle_optionnel_traduisible",
              "Libellé (optionnel, traduisible)",
            )}
            value={item.label}
            className={cn(CMS_PALETTE_TEXT_INPUT_CLASS, "h-9 w-full")}
            onChange={(e) => updateAt(index, { label: e.target.value })}
          />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addItem}>
        <Plus className="mr-1.5 h-3.5 w-3.5" />
        Ajouter un lien
      </Button>
    </div>
  );
}
